/*
* Project: BookingEngine
* Module: components/loyalty/LoyaltyEmailStatusHelp.js
*
* Mensaje que se muestra debajo del input de email según el status que regresa
* checkRegistrationEmail y el formulario en el que estamos (registro, login o forgot).
* Los CTAs van en el mismo párrafo que el mensaje.
*/
import React from 'react';
import PropTypes from 'prop-types';
import {
  EMAIL_FORM, EMAIL_STATUS, LOYALTY_SUPPORT_EMAIL,
} from '../../utils/loyaltyEmailStatus';
import './loyaltyEmailStatus.css';

const TONE = Object.freeze({
  ERROR: 'error',
  SUCCESS: 'success',
  INFO: 'info',
});

export const getEmailStatusTone = (form, status) => {
  if (!status) {
    return null;
  }
  if (form === EMAIL_FORM.REGISTER) {
    return status === EMAIL_STATUS.NOT_FOUND ? TONE.SUCCESS : TONE.ERROR;
  }
  if (form === EMAIL_FORM.FORGOT && status === EMAIL_STATUS.NOT_FOUND) {
    return TONE.ERROR;
  }
  if ([EMAIL_STATUS.PENDING_VERIFICATION, EMAIL_STATUS.UNSUBSCRIBED, EMAIL_STATUS.BLOCKED].indexOf(status) !== -1) {
    return TONE.ERROR;
  }
  // Login: ACTIVE / NOT_FOUND / INVALID_EMAIL. Forgot: ACTIVE / INVALID_EMAIL -> sin mensaje
  return null;
};

const ActionLink = ({ onClick, disabled, children }) => {
  const handleClick = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (!disabled && onClick) {
      onClick();
    }
  };
  const handleKeyDown = (ev) => {
    if (ev.key === 'Enter' || ev.key === ' ') {
      handleClick(ev);
    }
  };
  return (
    <span
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      className={`loyaltyEmailStatus__cta${disabled ? ' is-disabled' : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {children}
    </span>
  );
};

ActionLink.propTypes = {
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  children: PropTypes.node,
};

const buildContent = (props) => {
  const {
    t, form, status, loading, checkFailed, isResending, resendError,
    onLogin, onForgotPassword, onRegister, onResend,
  } = props;

  if (loading) {
    // Sólo el registro muestra el estado "validando"
    return form === EMAIL_FORM.REGISTER
      ? { tone: TONE.INFO, body: t('Checking email...') }
      : null;
  }

  if (checkFailed) {
    // Un error técnico del check no bloquea; sólo se avisa en el registro
    return form === EMAIL_FORM.REGISTER
      ? { tone: TONE.ERROR, body: t('We could not verify this email right now. Please try again in a few moments.') }
      : null;
  }

  const tone = getEmailStatusTone(form, status);
  if (!tone) {
    return null;
  }

  const resendCta = (label) => (
    <ActionLink onClick={onResend} disabled={isResending}>
      {isResending ? t('Sending...') : t(label)}
    </ActionLink>
  );

  const resendErrorLine = resendError
    ? <span className="loyaltyEmailStatus__line loyaltyEmailStatus__resendError">{resendError}</span>
    : null;

  switch (status) {
    case EMAIL_STATUS.NOT_FOUND:
      if (form === EMAIL_FORM.REGISTER) {
        return { tone, body: t('Valid email, you can register your account with this email.') };
      }
      return {
        tone,
        body: (
          <React.Fragment>
            {t('This email is not valid or is not registered.')}{' '}
            <ActionLink onClick={onRegister}>{t('Click to register')}</ActionLink>
          </React.Fragment>
        ),
      };

    case EMAIL_STATUS.INVALID_EMAIL:
      return {
        tone,
        body: (
          <React.Fragment>
            <span className="loyaltyEmailStatus__line">{t('Please enter a valid email address.')}</span>
            <span className="loyaltyEmailStatus__line">{t('Check the email spelling and try again.')}</span>
          </React.Fragment>
        ),
      };

    case EMAIL_STATUS.ACTIVE:
      return {
        tone,
        body: (
          <React.Fragment>
            {t('This email is already registered with an active account.')}{' '}
            <ActionLink onClick={onLogin}>{t('Click to log in')}</ActionLink>
            {' '}{t('or')}{' '}
            <ActionLink onClick={onForgotPassword}>{t('Click to receive password reset instructions')}</ActionLink>
          </React.Fragment>
        ),
      };

    case EMAIL_STATUS.PENDING_VERIFICATION:
      return {
        tone,
        body: (
          <React.Fragment>
            {t('This email has a registration in progress.')}{' '}
            {resendCta('Click to receive verification email.')}
            {resendErrorLine}
          </React.Fragment>
        ),
      };

    case EMAIL_STATUS.UNSUBSCRIBED:
      return {
        tone,
        body: (
          <React.Fragment>
            {t('This account was deactivated.')}{' '}
            {resendCta('Click to receive a new verification email and reactivate it.')}
            {resendErrorLine}
          </React.Fragment>
        ),
      };

    case EMAIL_STATUS.BLOCKED:
      return {
        tone,
        body: (
          <React.Fragment>
            {t('We were unable to continue with this account.')}{' '}
            {t('Please contact our support team at')}{' '}
            <a className="loyaltyEmailStatus__cta" href={`mailto:${LOYALTY_SUPPORT_EMAIL}`}>
              {LOYALTY_SUPPORT_EMAIL}
            </a>
          </React.Fragment>
        ),
      };

    default:
      return null;
  }
};

const LoyaltyEmailStatusHelp = (props) => {
  const content = buildContent(props);
  if (!content) {
    return null;
  }
  const className = [
    'loyaltyEmailStatus',
    `loyaltyEmailStatus--${content.tone}`,
    props.onDark ? 'loyaltyEmailStatus--onDark' : '',
  ].filter(Boolean).join(' ');

  return (
    <div id={props.id} className={className} aria-live="polite">
      <p className="loyaltyEmailStatus__text">{content.body}</p>
    </div>
  );
};

LoyaltyEmailStatusHelp.propTypes = {
  t: PropTypes.func.isRequired,
  form: PropTypes.oneOf([EMAIL_FORM.REGISTER, EMAIL_FORM.LOGIN, EMAIL_FORM.FORGOT]).isRequired,
  status: PropTypes.string,
  loading: PropTypes.bool,
  checkFailed: PropTypes.bool,
  onDark: PropTypes.bool,
  isResending: PropTypes.bool,
  resendError: PropTypes.string,
  onLogin: PropTypes.func,
  onForgotPassword: PropTypes.func,
  onRegister: PropTypes.func,
  onResend: PropTypes.func,
  id: PropTypes.string,
};

LoyaltyEmailStatusHelp.defaultProps = {
  status: null,
  loading: false,
  checkFailed: false,
  onDark: false,
  isResending: false,
  resendError: '',
  onLogin: null,
  onForgotPassword: null,
  onRegister: null,
  onResend: null,
  id: undefined,
};

// Clase para pintar el borde del input según el status (o '' si no aplica)
export const getEmailInputStatusClass = (form, status, loading, checkFailed) => {
  if (loading) {
    return '';
  }
  if (checkFailed) {
    return form === EMAIL_FORM.REGISTER ? 'loyaltyEmailInput--error' : '';
  }
  const tone = getEmailStatusTone(form, status);
  if (tone === TONE.SUCCESS) {
    return 'loyaltyEmailInput--success';
  }
  if (tone === TONE.ERROR) {
    return 'loyaltyEmailInput--error';
  }
  return '';
};

export default LoyaltyEmailStatusHelp;
